import React, { useEffect } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  O PARECER INTEIRO — a tela que o card não comporta
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O card do "Parecer financeiro" mostrava TRÊS blocos: evolução, estrutura e
 * liquidez. A Mia CFO escreve CINCO — faltavam `setorial` e `leitura_risco`,
 * que são justamente a posição frente aos pares e o juízo sobre o risco. Eles
 * eram gravados no banco e nunca chegavam à tela.
 *
 * Então este componente não existe só por causa de texto cortado. Ele existe
 * porque duas das cinco leituras não apareciam em lugar nenhum.
 *
 * Regra que ele carrega: o card mostra o que cabe, e aqui mostra TUDO — sem
 * reticência, sem corte, sem resumo. Quem decide o quanto aparece é a tela, e a
 * tela de dentro não tem limite de altura: ela rola.
 *
 * Fecha por: botão, clique no fundo, e tecla Esc.
 */

const Secao = ({ titulo, texto, C }) => (!texto ? null : (
  <div style={{ margin: '0 0 18px' }}>
    <div style={{
      fontSize: 10.5, letterSpacing: 0.8, color: C.muted,
      textTransform: 'uppercase', margin: '0 0 4px',
    }}>{titulo}</div>
    <div style={{ fontSize: 13, lineHeight: 1.65, color: C.ink }}>{texto}</div>
  </div>
));

const Lista = ({ titulo, itens, cor, C }) => (
  <div style={{
    flex: '1 1 260px', border: '1px solid ' + C.line, borderRadius: 10,
    borderLeft: '4px solid ' + cor, padding: '12px 14px', background: C.paper,
  }}>
    <div style={{
      fontSize: 10.5, letterSpacing: 0.8, color: cor,
      textTransform: 'uppercase', margin: '0 0 6px',
    }}>{titulo}</div>
    {(itens || []).length
      ? (itens || []).map((t, i) => (
          <div key={i} style={{ fontSize: 12.5, lineHeight: 1.5, padding: '3px 0', color: C.ink }}>- {t}</div>
        ))
      : <div style={{ fontSize: 12, color: C.muted }}>nenhum destaque</div>}
  </div>
);

export default function ParecerCompleto({ par, pontos, miaRisk, denom, dataBase, C, onFechar }) {
  useEffect(() => {
    const porEsc = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', porEsc);
    /* O fundo não rola enquanto a tela de cima está aberta. */
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', porEsc);
      document.body.style.overflow = antes;
    };
  }, [onFechar]);

  const p = par || {};
  const pts = pontos || {};
  const mr = miaRisk || null;
  const daIA = p.fonte === 'mia_cfo';

  /* `aparado_em` só existe quando a trava de guarda do backend precisou agir.
     Se um dia aparecer na tela, é sinal de que o modelo fugiu do contrato por
     uma ordem de grandeza — e isso é para ser visto, não escondido. */
  const aparado = Array.isArray(p.aparado_em) && p.aparado_em.length ? p.aparado_em : null;

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(10, 22, 40, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: C.paper, border: '1px solid ' + C.line, borderRadius: 12,
          width: '100%', maxWidth: 880, padding: '24px 28px 28px',
          boxShadow: '0 18px 48px rgba(10, 22, 40, 0.28)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, margin: '0 0 6px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>Parecer financeiro</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
              {denom ? denom + ' · ' : ''}{dataBase || p.data_base || ''}
              {' · '}
              {daIA ? 'gerado por Mia CFO' : 'Mia CFO - sintese quantitativa'}
            </div>
          </div>
          <button
            type="button"
            onClick={onFechar}
            style={{
              border: '1px solid ' + C.line, background: C.paper, color: C.navy,
              borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >Fechar</button>
        </div>

        <div style={{ height: 1, background: C.line, margin: '14px 0 18px' }} />

        <Secao titulo="Evolucao" texto={p.evolucao} C={C} />
        <Secao titulo="Estrutura de capital" texto={p.estrutura} C={C} />
        <Secao titulo="Liquidez e cobertura" texto={p.liquidez} C={C} />
        <Secao titulo="Posicao setorial" texto={p.setorial} C={C} />
        <Secao titulo="Leitura de risco" texto={p.leitura_risco} C={C} />

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '4px 0 0' }}>
          <Lista titulo="Pontos de sustentacao" itens={pts.sustentacao} cor={C.green} C={C} />
          <Lista titulo="Pontos de atencao" itens={pts.atencao} cor={C.red} C={C} />
        </div>

        {mr && (mr.fator || mr.direcao) ? (
          <div style={{
            marginTop: 14, border: '1px solid ' + C.line, borderRadius: 10,
            padding: '12px 14px', background: C.cream,
          }}>
            <div style={{
              fontSize: 10.5, letterSpacing: 0.8, color: C.muted,
              textTransform: 'uppercase', margin: '0 0 4px',
            }}>Leitura da Mia Risk</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, color: C.ink }}>
              {mr.fator || '-'}
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>
              direcao: {mr.direcao || 'n/d'}
              {mr.confidence !== null && mr.confidence !== undefined
                ? ' · confianca: ' + (100 * mr.confidence).toFixed(0) + '%'
                : ''}
            </div>
            {/* Ausente DE PROPOSITO: notch e multiplicador de PD nao saem de
                gerador de texto. A leitura e insumo; a politica e de credito. */}
          </div>
        ) : null}

        {aparado ? (
          <div style={{ marginTop: 14, fontSize: 11.5, color: C.amber2 }}>
            Blocos aparados pela trava de guarda: {aparado.join(', ')}.
          </div>
        ) : null}
      </div>
    </div>
  );
}
